/**
 * Avertit le client, avant le passage en caisse, que les josettes marionnettes
 * présentes dans son panier sont vendues à l'unité.
 *
 * Les lignes concernées sont marquées côté Liquid (snippets/marionnette-attr.liquid)
 * parce que /cart.js ne renvoie pas les metafields produit. Ces marqueurs sont
 * re-rendus avec le panier, ils reflètent donc toujours son état courant.
 *
 * Fonctionne pour le tiroir panier comme pour la page /cart.
 * Les boutons de paiement accéléré (PayPal, Shop Pay…) ne passent pas par le
 * formulaire du thème et ne sont donc pas interceptés.
 */
const MARIONNETTE_CART_FORM_IDS = ["CartDrawer", "cart"];

class MarionnetteGuard extends HTMLElement {
  connectedCallback() {
    if (this.submitHandler) return;
    this.submitHandler = this.handleSubmit.bind(this);
    // En capture : il faut décider avant les gestionnaires du thème.
    document.addEventListener("submit", this.submitHandler, true);
  }

  disconnectedCallback() {
    if (!this.submitHandler) return;
    document.removeEventListener("submit", this.submitHandler, true);
    this.submitHandler = null;
  }

  get modal() {
    // ModalDialog se déplace sur <body> en se connectant : on le retrouve par id.
    return document.getElementById("PopupModal-MarionnetteGuard");
  }

  handleSubmit(event) {
    if (this.bypass) return;

    const form = event.target;
    if (!form || !MARIONNETTE_CART_FORM_IDS.includes(form.id)) return;

    // Le même formulaire sert aussi à mettre le panier à jour : seul l'envoi
    // déclenché par « Passer à la caisse » nous intéresse.
    const submitter = event.submitter;
    if (!submitter || submitter.name !== "checkout") return;

    if (!this.hasMarionnette(form)) return;

    event.preventDefault();
    this.open(form, submitter);
  }

  hasMarionnette(form) {
    // Le tiroir n'est pas rendu sur /cart : les deux paniers ne coexistent
    // jamais, mais on scope quand même le tiroir à lui-même.
    const scope = form.id === "CartDrawer" ? form.closest("cart-drawer") : document;
    return Boolean(scope && scope.querySelector('[data-marionnette="true"]'));
  }

  open(form, submitter) {
    const modal = this.modal;

    // Pas de modale disponible : on ne bloque pas la vente.
    if (!modal) {
      this.submitAnyway(form, submitter);
      return;
    }

    this.pending = { form, submitter };
    this.bindModal(modal);
    modal.show(submitter);
  }

  bindModal(modal) {
    if (this.modalBound) return;
    this.modalBound = true;

    const continueButton = modal.querySelector("[data-marionnette-guard-continue]");
    if (continueButton) {
      continueButton.addEventListener("click", () => {
        const pending = this.pending;
        modal.hide();
        if (pending) this.submitAnyway(pending.form, pending.submitter);
      });
    }

    modal.addEventListener("close", () => {
      this.pending = null;
      // ModalDialog rend le scroll au body en se fermant, même quand le tiroir
      // panier est toujours ouvert derrière : on le reverrouille dans ce cas.
      if (document.querySelector("drawer-component[open]")) {
        document.body.classList.add("prevent-scroll");
      }
    });
  }

  submitAnyway(form, submitter) {
    this.bypass = true;
    // requestSubmit conserve le submitter, donc le name="checkout" attendu par
    // Shopify. Sans lui, le formulaire repartirait vers /cart.
    if (typeof form.requestSubmit === "function") {
      form.requestSubmit(submitter);
    } else {
      submitter.click();
    }
    // La navigation part, mais on ne laisse pas le contournement armé si elle échoue.
    setTimeout(() => {
      this.bypass = false;
    });
  }
}

customElements.define("marionnette-guard", MarionnetteGuard);
